import clsx from "clsx";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import WModal from "../wmodal/WModal";
import Image from "next/image";

/* eslint-disable-next-line */
export interface LReviewCardProps {
  title?: string;
  content?: string; //rich text
  authorPicture?: {
    url: string;
    altTag: string;
    formats?: any;
  };
  authorName?: string;
  authorLocation?: string;
  authorOccupations?: string;
  showPictureAsCircle?: boolean;
  numberOfChars?: number;
  size?: "small" | "medium" | "big";
  hideImage?: boolean;
  textColor?: string;
  itemBoxColor?: string;
  itemBoxTextColor?: string;
}

export function LReviewCard(props: LReviewCardProps) {
  const { t: tCommon } = useTranslation("common");
  const [truncateLimit, setTruncateLimit] = useState<number>(
    props?.numberOfChars ?? 215
  );
  const [modal, setModal] = useState("");
  const openReviewModal = useCallback(() => {
    return "hi";
  }, []);
  return (
    <>
      <div
        className={clsx(
          "flex-shrink-0 ites flex  flex-col rounded-xl overflow-hidden h-full",
          "snap-start sm:snap-center",
          {
            "w-[300px] lg:w-[382px]": props?.hideImage,
            "w-auto":
              !props?.hideImage && (props?.size === "big" || !props?.size),
            "w-[250px] lg:w-[420px]":
              !props?.hideImage && props?.size === "medium",
            "w-[200px] lg:w-[300px]":
              !props?.hideImage && props?.size === "small",
          }
        )}
      >
        {!props?.hideImage && props?.authorPicture?.url && (
          <div
            className={clsx("relative", {
              "h-[400px] w-[420px]":
                !props?.showPictureAsCircle &&
                (props?.size === "big" || !props?.size),
              "h-[420px] w-[420px]":
                props?.showPictureAsCircle &&
                (props?.size === "big" || !props?.size),
              "h-[250px] w-[250px]":
                props?.showPictureAsCircle && props?.size === "medium",
              "h-[212px] w-[250px] lg:h-[280px] lg:w-[420px]":
                !props?.showPictureAsCircle && props?.size === "medium",
              "h-[200px] w-[200px]":
                props?.showPictureAsCircle && props?.size === "small",
              "h-[170px] w-[200px] lg:h-[200px] lg:w-[300px]":
                !props?.showPictureAsCircle && props?.size === "small",
            })}
          >
            <Image
              src={
                props?.authorPicture?.formats?.large?.url ||
                props?.authorPicture?.url
              }
              fill
              sizes="(max-width: 1024px) 100vw, 66vw"
              alt={props?.authorPicture?.altTag || "image"}
              className={clsx("block object-cover cursor-pointer", {
                "rounded-full": props?.showPictureAsCircle,
              })}
              onClick={() => setModal("fullReview")}
              quality={80}
            />
          </div>
        )}
        <div
          className={clsx("", {
            "max-w-[420px]": props?.size === "big" || !props?.size,
            "max-w-[300px]":
              props?.size === "small" || props?.size === "medium",
          })}
        >
          <div
            className={clsx("pb-3 px-3", {
              "bg-eklight": !props?.showPictureAsCircle && !props?.itemBoxColor,
              "rounded-b-xl": !props?.showPictureAsCircle,
              "rounded-xl": !props?.showPictureAsCircle && props?.hideImage,
            })}
            style={
              !props?.showPictureAsCircle
                ? {
                    backgroundColor: props?.itemBoxColor,
                    color: props?.itemBoxTextColor,
                  }
                : {}
            }
          >
            {props?.title && (
              <div
                className="pt-2 text-2xl font-extrabold leading-tight cursor-pointer text-center lg:text-left"
                onClick={() => setModal("fullReview")}
              >
                {props?.title?.length > 45
                  ? `${props?.title?.slice(0, 45)}...`
                  : props?.title}
              </div>
            )}
            {props?.content && (
              <div className="pt-3 text-sm text-center lg:text-left">
                {props?.content?.length > truncateLimit ? (
                  <ReactMarkdown>
                    {`${props?.content?.slice(0, truncateLimit)}...`}
                  </ReactMarkdown>
                ) : (
                  <ReactMarkdown>{props?.content}</ReactMarkdown>
                )}

                {props?.content?.length > truncateLimit && (
                  <div
                    className="pt-2 font-bold cursor-pointer hover:text-ekgreen"
                    onClick={() => setModal("fullReview")}
                  >
                    {tCommon("show-more")}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <div>
                {props?.authorName && (
                  <div className="text-lg font-semibold">
                    {props?.authorName}
                  </div>
                )}
                {props?.authorOccupations && (
                  <div className="text-sm">{props?.authorOccupations}</div>
                )}
                {props?.authorLocation && (
                  <div className="text-sm text-ekgray">
                    {props?.authorLocation}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {modal === "fullReview" && (
        <WModal large setModal={setModal} backgroundColor={props?.itemBoxColor}>
          <div
            style={{
              color:
                props?.itemBoxTextColor ||
                "rgb(19 35 91 / var(--tw-text-opacity))",
            }}
          >
            <div className="flex gap-5">
              <div className="flex-shrink-0 hidden lg:block w-72 aspect-square overflow-hidden">
                <img
                  src={props?.authorPicture?.url}
                  alt={props?.authorPicture?.altTag}
                  className={clsx(
                    "object-cover w-full h-full",
                    props?.showPictureAsCircle ? "!rounded-full" : "rounded-lg"
                  )}
                />
              </div>
              <div className="flex flex-col gap-3">
                <div className="text-2xl font-extrabold leading-tight text-center lg:text-left">
                  {props?.title}
                </div>
                <div className="small-markdown">
                  <ReactMarkdown>{props?.content ?? ""}</ReactMarkdown>
                </div>
                <div className="flex gap-3 mt-5">
                  <div
                    className={clsx([
                      props?.showPictureAsCircle
                        ? "rounded-full"
                        : "rounded-lg",
                      "lg:hidden overflow-hidden w-full aspect-square",
                    ])}
                  >
                    <img
                      src={props?.authorPicture?.url}
                      alt={props?.authorPicture?.altTag}
                      className={clsx(
                        "object-cover w-full h-full",
                        props?.showPictureAsCircle && "rounded-full"
                      )}
                    />
                  </div>
                  <div>
                    {props?.authorName && (
                      <div className="text-lg font-semibold">
                        {props?.authorName}
                      </div>
                    )}
                    {props?.authorOccupations && (
                      <div className="text-sm">{props?.authorOccupations}</div>
                    )}
                    {props?.authorLocation && (
                      <div className="text-sm text-ekgray">
                        {props?.authorLocation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </WModal>
      )}
    </>
  );
}

export default LReviewCard;
